// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./RoleConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import TanStackTable from "../../Misc/Table/TanStackTable";
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import EditRoleDialog from "./EditRoleDialog/EditRoleDialog";
import NewRoleDialog from "./NewRoleDialog/NewRoleDialog";
import BulkAssignToolDialog from "./BulkAssignToolDialog/BulkAssignToolDialog";

// CONSTANTS
import {
	defaultSort,
	splitSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

const RoleConsole = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	// Data
	const [roles, setRoles] = useState([]);
	const [tools, setTools] = useState([]);
	const [selectedTools, setSelectedTools] = useState([]);
	const [selectedAdminTools, setSelectedAdminTools] = useState([]);

	// Table
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	// Dialog
	const [newOpen,  setNewOpen]  = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
	const [recordToEdit, setRecordToEdit] = useState({});

	// Loading
	const [loading, setLoading] = useState(false);

	// Access log
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Role Console",
			employeeID: employeeID,
			notes: "Accessed the Role Console page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Fetch on mount
	useEffect(() => {
		fetchRoles();
	}, []);

	// Rebuild table when roles or tools change
	useEffect(() => {
		if (roles.length > 0 && tools.length > 0) {
			buildColumns();
			buildRows();
		}
	}, [roles, tools]);

	// API: load roles & tools
	const fetchRoles = async () => {
		setLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/roles/all"
			);
			setRoles(res.data);
			await fetchTools();
		} catch (err) {
			console.error("Error fetching roles:", err);
		}
		setLoading(false);
	};

	const fetchTools = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/roles/tools/all"
			);
			setTools(res.data);
		} catch (err) {
			console.error("Error fetching tools:", err);
		}
	};

	const fetchSelectedTools = async (roleID, endpoint, setter) => {
		try {
			const res = await getAPICall(instance, accounts[0], endpoint, {
				roleID,
			});
			setter(res.data);
		} catch (err) {
			console.error("Error fetching selected tools:", err);
		}
	};

	// Open edit dialog
	const openEdit = async (role) => {
		await fetchSelectedTools(
			role.id,
			"/api/roles/tools/byRole",
			setSelectedTools
		);
		await fetchSelectedTools(
			role.id,
			"/api/roles/tools/admin/byRole",
			setSelectedAdminTools
		);
		setRecordToEdit(role);
		setEditOpen(true);
	};
	const closeEdit = () => setEditOpen(false);

	// Build table columns
	const buildColumns = () => {
		setColumns([
			{
				accessorKey: "role_name",
				header: () => <span>Role Name</span>,
				label: "Role Name",
				cell: (props) => (
					<span>{props.getValue() ?? notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "split_tools",
				header: () => <span>Tools</span>,
				label: "Tools",
				cell: (props) =>
					props.getValue()
						? props
								.getValue()
								.split("_SPLIT_")
								.map((t, i) => <div key={i}>{t}</div>)
						: notApplicable,
				filterType: "checkbox",
				filterFn: splitSort,
				filterOptions: tools.map((t) => ({
					label: t.name,
					value: t.name,
				})),
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => openEdit(row.original.record)}
						disableRipple={true}
						sx={{
							justifyContent: "flex-end",
						}}
					>
						<EditIcon fontSize="inherit" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	// Build table rows
	const buildRows = () => {
		setRows(
			roles.map((r) => ({
				role_name: r.role_name,
				split_tools: r.split_tools,
				record: r,
			}))
		);
	};

	return (
		<div className="RoleConsole">
			<div className="roleConsoleHeader">
				<div className="roleConsoleHeaderTitle">
					<span>Role Console</span>
					<div className="roleConsoleHeaderActions">
						<Button
							variant="contained"
							color="primary"
							onClick={() => setNewOpen(true)}
							disabled={loading}
						>
							New Record
						</Button>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setBulkAssignOpen(true)}
							disabled={loading}
						>
							Assign Tool to Roles
						</Button>
					</div>
				</div>
			</div>

			{loading ? (
				<div className="loadingDiv">
					<CircularProgress />
				</div>
			) : roles.length === 0 ? (
				<J2NoResultsFound />
			) : (
				<div className="tableContainerDiv">
					<TanStackTable columnData={columns} rowData={rows} />
				</div>
			)}

			<NewRoleDialog
				open={newOpen}
				tools={tools}
				newRoleDialogClosed={() => setNewOpen(false)}
				fetchRoles={fetchRoles}
			/>

			<EditRoleDialog
				open={editOpen}
				tools={tools}
				selectedTools={selectedTools}
				selectedAdminTools={selectedAdminTools}
				editRoleDialogClosed={closeEdit}
				recordToEdit={recordToEdit}
				fetchRoles={fetchRoles}
			/>

			<BulkAssignToolDialog
				open={bulkAssignOpen}
				tools={tools}
				roles={roles}
				bulkAssignToolDialogClosed={() => setBulkAssignOpen(false)}
				fetchRoles={fetchRoles}
			/>
		</div>
	);
};

export default RoleConsole;
